import React, { useState } from 'react';

import log from 'loglevel';
import { Building2, Clock, Users } from 'lucide-react';

import { useTenant } from '../../contexts/TenantContext';
import { AuthBrand } from '../AuthCardChrome/AuthCardChrome';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';

import styles from './TenantSelector.module.css';

export const TenantSelector: React.FC = () => {
  const { availableTenants, selectTenant, isLoading, error } = useTenant();
  const [selectedId, setSelectedId] = useState<string>('');
  const [isSelecting, setIsSelecting] = useState(false);

  const handleSelect = async () => {
    if (!selectedId || isSelecting) return;

    try {
      setIsSelecting(true);
      await selectTenant(selectedId);
      // Navigation handled by TenantContext
    } catch (err) {
      log.error('Failed to select organization:', err);
      setIsSelecting(false);
    }
  };

  const formatDate = (date?: Date | string) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString();
  };

  const getRoleColor = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin':
        return 'role-admin';
      case 'manager':
        return 'role-manager';
      case 'user':
        return 'role-user';
      default:
        return 'role-default';
    }
  };

  if (isLoading) {
    return <LoadingOverlay />;
  }

  if (error) {
    return (
      <div className={styles.tenantSelectorContainer}>
        <div className={styles.tenantSelectorCard}>
          <div className={styles.errorState}>
            <h1>Unable to load your organizations</h1>
            <p className={styles.errorMessage}>
              {error.message || 'An error occurred while loading your organizations.'}
            </p>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (availableTenants.length === 0) {
    return (
      <div className={styles.tenantSelectorContainer}>
        <div className={styles.tenantSelectorCard}>
          <div className={styles.noAccessState}>
            <Building2 className={styles.noAccessIcon} />
            <h1>No Access</h1>
            <p className={styles.subtitle}>You don't have access to any organizations yet.</p>
            <p className={styles.helpText}>
              Please contact your administrator to get access to an organization.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tenantSelectorContainer}>
      <div className={styles.tenantSelectorCard}>
        <div className={styles.header}>
          <AuthBrand />
          <h1>Pick your organization</h1>
          <p className={styles.subtitle}>Choose which organization you want to work with</p>
        </div>

        <div className={styles.tenantList}>
          {availableTenants.map((userTenant) => (
            <div
              key={userTenant.id}
              className={`${styles.tenantCard} ${selectedId === userTenant.tenantId ? styles.selected : ''}`}
              onClick={() => setSelectedId(userTenant.tenantId)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedId(userTenant.tenantId);
                }
              }}
              tabIndex={0}
              role="button"
              aria-label={`Select ${userTenant.tenant.name}`}
            >
              <div className={styles.tenantMain}>
                <div className={styles.tenantInfo}>
                  <h3 className={styles.tenantName}>{userTenant.tenant.name}</h3>
                  {userTenant.tenant.description && (
                    <p className={styles.tenantDescription}>{userTenant.tenant.description}</p>
                  )}
                </div>
                <div className={styles.tenantMeta}>
                  <span className={`${styles.roleBadge} ${styles[getRoleColor(userTenant.role)]}`}>
                    <Users className={styles.roleIcon} />
                    {userTenant.role}
                  </span>
                </div>
              </div>

              {userTenant.lastAccessedAt && (
                <div className={styles.tenantFooter}>
                  <Clock className={styles.clockIcon} />
                  <span className={styles.lastAccessed}>
                    Last accessed: {formatDate(userTenant.lastAccessedAt)}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={handleSelect}
            disabled={!selectedId || isSelecting}
          >
            {isSelecting ? 'Loading...' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};
